import {markdownToHTML} from '@pinecast/common/markdown';
import type {CSS} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

// Styletron only nests style objects whose key starts with `:` (or `@media`);
// anything else — `& p` and friends — is silently dropped. Descendant rules
// therefore lead with a pseudo-class that always matches, and each selector
// gets its own key: only the first one in a comma-separated key would be
// scoped to this element's class, and the rest would leak page-wide.
const headingStyles = {
  fontSize: 15,
  fontWeight: 600,
  marginBottom: 8,
  marginTop: 16,
};
const listStyles = {paddingLeft: 20};
const Body = styled('div', (_, designColors) => ({
  color: designColors.contentText,
  fontSize: 14,
  lineHeight: 22,
  ':not(:empty) h1': {...headingStyles},
  ':not(:empty) h2': {...headingStyles},
  ':not(:empty) h3': {...headingStyles},
  ':not(:empty) h4': {...headingStyles},
  ':not(:empty) h5': {...headingStyles},
  ':not(:empty) h6': {...headingStyles},
  ':not(:empty) ul': {...listStyles},
  ':not(:empty) ol': {...listStyles},
  ':not(:empty) li': {
    marginBottom: 4,
  },
  ':not(:empty) p': {
    marginBottom: 8,
    marginTop: 0,
  },
  // A quotation that a creator wrote as raw HTML stays inside the body. The bar of
  // a quotation has the color of muted text, which clears 3:1 on the surface in
  // both color modes.
  ':not(:empty) blockquote': {
    borderLeft: `3px solid ${designColors.contentTextMuted}`,
    marginBottom: 8,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    paddingLeft: 12,
  },
  // Markdown links are bare `<a>`s, not `Link`s, so nothing else colors them:
  // left alone they fall back to the browser's blue, which sits at 1.3:1 on the
  // dark content background. Same treatment as `Link`.
  ':not(:empty) a': {
    color: designColors.headingBlue,
    textDecoration: 'underline',
    ':hover': {
      color: designColors.contentTextMuted,
    },
  },
  ':not(:empty) > :first-child': {
    marginTop: 0,
  },
  ':not(:empty) > :last-child': {
    marginBottom: 0,
  },
}));

type Props = {
  /**
   * Raw markdown. `markdownToHTML` renders and sanitizes it, so callers do not
   * need to sanitize the result themselves.
   */
  markdown: string;
  id?: string;
  /**
   * The language of the text, when it is not the language of the page (WCAG
   * 3.1.2). The text then also has `dir="auto"`: a language of another page can
   * be one that runs from right to left, and the direction follows the text.
   */
  lang?: string;
  style?: CSS;
};

/**
 * Renders a markdown body — an update, a report, an editor preview — with the
 * dashboard's typography. The one place these rules live, so every surface
 * showing user-authored markdown reads the same.
 */
const MarkdownBody = React.forwardRef<HTMLElement, Props>(
  ({markdown, lang, ...rest}, ref) => {
    // Rendering the markdown also resolves the character references the editor
    // leaves in stored bodies (`I&#x27;m`), which showed through raw before.
    const html = React.useMemo(() => markdownToHTML(markdown), [markdown]);
    return (
      <Body
        {...rest}
        // The `lang` and the direction go together, as in `PodcastString`.
        {...(lang ? {dir: 'auto', lang} : {})}
        dangerouslySetInnerHTML={{__html: html}}
        ref={ref}
      />
    );
  },
);
MarkdownBody.displayName = 'MarkdownBody';

export default MarkdownBody;
